class ReviewsSlider extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('.reviews-slider__list');
    this.dotsContainer = this.querySelector('.reviews-slider__dots');

    if (!this.list) return;

    this._drag = { active: false, startX: 0, scrollLeft: 0 };

    this._buildDots();
    this._bindEvents();
    this._updateDots();

    this._resizeObserver = new ResizeObserver(() => {
      this._buildDots();
      this._updateDots();
    });
    this._resizeObserver.observe(this);
  }

  disconnectedCallback() {
    this._resizeObserver?.disconnect();
  }

  get _items() {
    return [...this.querySelectorAll('.reviews-slider__item')];
  }

  get _isMobile() {
    return window.innerWidth < 990;
  }

  _getItemsPerPage() {
    if (this._isMobile) return 1;
    const item = this._items[0];
    if (!item) return 1;
    const step = item.offsetWidth + parseFloat(getComputedStyle(item).marginRight);
    return step > 0 ? Math.max(1, Math.floor(this.list.clientWidth / step)) : 1;
  }

  _getTotalPages() {
    const count = this._items.length;
    return count > 0 ? Math.ceil(count / this._getItemsPerPage()) : 0;
  }

  _getCurrentPage() {
    const totalPages = this._getTotalPages();
    if (totalPages <= 1) return 0;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    if (maxScroll <= 0) return 0;
    const progress = this.list.scrollLeft / maxScroll;
    return Math.min(Math.round(progress * (totalPages - 1)), totalPages - 1);
  }

  _buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';

    const totalPages = this._getTotalPages();

    if (totalPages <= 1) {
      this.dotsContainer.hidden = true;
      return;
    }

    this.dotsContainer.hidden = false;

    for (let i = 0; i < totalPages; i++) {
      const dot = document.createElement('button');
      dot.className = 'reviews-slider__dot';
      dot.setAttribute('aria-label', `Go to page ${i + 1}`);
      dot.addEventListener('click', () => this._goToPage(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  _updateDots() {
    if (!this.dotsContainer) return;
    const current = this._getCurrentPage();
    this.dotsContainer.querySelectorAll('.reviews-slider__dot').forEach((dot, i) => {
      dot.classList.toggle('reviews-slider__dot--active', i === current);
    });
  }

  _goToPage(index) {
    const totalPages = this._getTotalPages();
    if (totalPages <= 1) return;
    const maxScroll = this.list.scrollWidth - this.list.clientWidth;
    this.list.scrollTo({
      left: (index / (totalPages - 1)) * maxScroll,
      behavior: 'smooth',
    });
  }

  _bindEvents() {
    this.list.addEventListener('scroll', () => this._updateDots(), { passive: true });
    this.list.addEventListener('dragstart', (e) => e.preventDefault());

    this.list.addEventListener('mousedown', (e) => {
      if (this._isMobile) return;
      this._drag.active = true;
      this._drag.startX = e.pageX;
      this._drag.scrollLeft = this.list.scrollLeft;
      this.list.style.cursor = 'grabbing';
      this.list.style.scrollBehavior = 'auto';
    });

    this.list.addEventListener('mousemove', (e) => {
      if (!this._drag.active) return;
      e.preventDefault();
      this.list.scrollLeft = this._drag.scrollLeft - (e.pageX - this._drag.startX);
    });

    const endDrag = () => {
      if (!this._drag.active) return;
      this._drag.active = false;
      this.list.style.cursor = '';
      this.list.style.scrollBehavior = '';
    };

    this.list.addEventListener('mouseup', endDrag);
    this.list.addEventListener('mouseleave', endDrag);
  }
}

customElements.define('reviews-slider', ReviewsSlider);
